import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Alert,
  Platform,
} from 'react-native';
import { createChildProfile, ChildProfile, calculateAgeInMonths } from '../../types/profile';
import { useUserStore } from '../../contexts/StoreContext';
import { appConfig } from '../../config/configManager';
import { DatePickerModal } from '../DatePickerModal';

interface AddProfileViewProps {
  onProfileCreated: (profile: ChildProfile) => void;
  onCancel: () => void;
}

export const AddProfileView: React.FC<AddProfileViewProps> = ({
  onProfileCreated,
  onCancel,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    dob: new Date(2024, 11, 1), // December 1, 2024 - about 1 year old
    gender: '' as 'male' | 'female' | '',
    cdc_premature: 0 as 0 | 1,
    cdc_premgest: 40,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showDatePicker, setShowDatePicker] = useState(false);
  const gestationScrollRef = useRef<ScrollView>(null);
  const gestationScrollXRef = useRef(0);
  
  // Default user email — from the authenticated account session
  const userStore = useUserStore();
  const defaultUserEmail = userStore?.()?.session?.user?.email ?? '';

  // Helper function to format date for display
  const formatDateForDisplay = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const toDatePickerValue = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fromDatePickerValue = (value: string): Date => {
    const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return formData.dob;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    return new Date(year, month - 1, day);
  };

  const ageInMonths = calculateAgeInMonths(formData.dob.toISOString());
  const isUnder24Months = Number.isFinite(ageInMonths) && ageInMonths < 24;
  const premWeeks = formData.cdc_premature === 1 ? Math.max(0, 40 - formData.cdc_premgest) : 0;
  const gestationOptions = Array.from({ length: 20 }, (_, i) => 40 - i); // 40..21

  const scrollGestationBy = (deltaX: number) => {
    const next = Math.max(0, gestationScrollXRef.current + deltaX);
    gestationScrollRef.current?.scrollTo({ x: next, animated: true });
    gestationScrollXRef.current = next;
  };

  useEffect(() => {
    if (!isUnder24Months) {
      setFormData((current) => ({ ...current, cdc_premature: 0, cdc_premgest: 40 }));
    }
  }, [isUnder24Months]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Profile name is required';
    }

    // Validate date of birth
    const now = new Date();
    if (formData.dob > now) {
      newErrors.dob = 'Date of birth cannot be in the future';
    }
    
    const maxAge = new Date(now.getFullYear() - 18, now.getMonth(), now.getDate());
    if (formData.dob < maxAge) {
      newErrors.dob = 'Child must be under 18 years old';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    try {
      // Generate a unique account number
      const accountNum = `CHILD${Date.now().toString().slice(-6)}`;

      // Create the profile using the factory function
      const profileWithoutId = createChildProfile({
        name: formData.name.trim(),
        email: defaultUserEmail, // Use app user's email by default
        dob: formData.dob.toISOString(),
        accountnum: accountNum,
        gender: (formData.gender as 'male' | 'female') || 'other', // Default to 'other' if not selected
        cdc_premature: isUnder24Months ? formData.cdc_premature : 0,
        cdc_premgest: isUnder24Months && formData.cdc_premature === 1 ? formData.cdc_premgest : null,
        cdc_premweek: isUnder24Months && formData.cdc_premature === 1 ? premWeeks : null,
        isPremium: false, // App subscription controls this
        devfloorCeilingTimestamp: {
          communication: new Date().toISOString(),
          motor: new Date().toISOString(),
          finemotor: new Date().toISOString(),
          grossmotor: new Date().toISOString(),
          cognitive: new Date().toISOString(),
          socialemotional: new Date().toISOString(),
        },
      });

      // Add an ID to complete the profile
      const newProfile: ChildProfile = {
        ...profileWithoutId,
        id: `profile_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      };

      onProfileCreated(newProfile);
    } catch (error) {
      Alert.alert('Error', 'Failed to create profile. Please try again.');
      console.error('Profile creation error:', error);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.backgroundImageContainer}>
        <View style={styles.backgroundOverlay} />
      </View>
      <ScrollView style={styles.scrollContainer} contentContainerStyle={styles.contentContainer}>
      <View style={{ maxWidth: appConfig.getSingleCardMaxWidth(), width: '100%', alignSelf: 'center' }}>
      <View style={styles.header}>
        <Text style={styles.title}>Add New Child Profile</Text>
        <Text style={styles.subtitle}>
          Create a profile to track your child's development milestones
        </Text>
      </View>

      <View style={styles.form}>
        {/* Name Field */}
        <View style={styles.fieldContainer}>
          <Text style={styles.label}>Child's Profile Name *</Text>
          <TextInput
            style={[styles.input, errors.name ? styles.inputError : null]}
            value={formData.name}
            onChangeText={(text) => setFormData({ ...formData, name: text })}
            placeholder="Name for this profile"
            autoCapitalize="words"
          />
          {errors.name && <Text style={styles.errorText}>{errors.name}</Text>}
        </View>

        {/* Date of Birth */}
        <View style={styles.fieldContainer}>
          <Text style={styles.label}>Date of Birth *</Text>
          <TouchableOpacity
            style={[styles.dateInput, errors.dob ? styles.inputError : null]}
            onPress={() => setShowDatePicker(true)}
          >
            <Text style={styles.dateText}>
              {formatDateForDisplay(formData.dob)}
            </Text>
          </TouchableOpacity>
          {errors.dob && <Text style={styles.errorText}>{errors.dob}</Text>}
          <Text style={styles.helpText}>
            Tap to select date of birth
          </Text>
        </View>

        {isUnder24Months && (
          <View style={styles.fieldContainer}>
            <TouchableOpacity
              style={styles.checkboxRow}
              onPress={() => setFormData((current) => ({
                ...current,
                cdc_premature: current.cdc_premature === 1 ? 0 : 1,
                cdc_premgest: current.cdc_premature === 1 ? 40 : current.cdc_premgest,
              }))}
            >
              <View style={[styles.checkboxBox, formData.cdc_premature === 1 && styles.checkboxBoxChecked]}>
                {formData.cdc_premature === 1 && <Text style={styles.checkboxCheck}>✓</Text>}
              </View>
              <Text style={styles.checkboxLabel}>Born Premature</Text>
            </TouchableOpacity>

            {formData.cdc_premature === 1 && (
              <View style={{ marginTop: 10 }}>
                <Text style={styles.label}>Gestation age at birth</Text>
                <View style={[styles.gestationScrollRow, Platform.OS !== 'web' && styles.gestationScrollRowMobile]}>
                  {Platform.OS === 'web' && (
                    <TouchableOpacity
                      style={styles.gestationScrollButton}
                      onPress={() => scrollGestationBy(-140)}
                    >
                      <Text style={styles.gestationScrollButtonText}>◀</Text>
                    </TouchableOpacity>
                  )}
                  <ScrollView
                    ref={gestationScrollRef}
                    horizontal
                    showsHorizontalScrollIndicator={Platform.OS === 'web'}
                    style={styles.gestationScrollView}
                    onScroll={(event) => {
                      gestationScrollXRef.current = event.nativeEvent.contentOffset.x;
                    }}
                    scrollEventThrottle={16}
                  >
                    <View style={styles.weekChipRow}>
                      {gestationOptions.map((week) => (
                        <TouchableOpacity
                          key={week}
                          style={[styles.weekChip, formData.cdc_premgest === week && styles.weekChipActive]}
                          onPress={() => setFormData((current) => ({ ...current, cdc_premgest: week }))}
                        >
                          <Text style={[styles.weekChipText, formData.cdc_premgest === week && styles.weekChipTextActive]}>{week}w</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </ScrollView>
                  {Platform.OS === 'web' && (
                    <TouchableOpacity
                      style={styles.gestationScrollButton}
                      onPress={() => scrollGestationBy(140)}
                    >
                      <Text style={styles.gestationScrollButtonText}>▶</Text>
                    </TouchableOpacity>
                  )}
                </View>
                <Text style={styles.helpText}>Weeks premature: {premWeeks}</Text>
              </View>
            )}
          </View>
        )}

        {/* Gender Selection */}
        <View style={styles.fieldContainer}>
          <Text style={styles.label}>Gender</Text>
          <View style={styles.genderContainer}>
            {(['male', 'female'] as const).map((gender) => (
              <TouchableOpacity
                key={gender}
                style={[
                  styles.genderButton,
                  formData.gender === gender ? styles.genderButtonSelected : null,
                ]}
                onPress={() => setFormData({ ...formData, gender })}
              >
                <Text
                  style={[
                    styles.genderButtonText,
                    formData.gender === gender ? styles.genderButtonTextSelected : null,
                  ]}
                >
                  {gender.charAt(0).toUpperCase() + gender.slice(1)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
          {errors.gender && <Text style={styles.errorText}>{errors.gender}</Text>}
        </View>

        {/* Action Buttons */}
        <View style={styles.buttonContainer}>
          <TouchableOpacity style={styles.cancelButton} onPress={onCancel}>
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.createButton} onPress={handleSubmit}>
            <Text style={styles.createButtonText}>Create Profile</Text>
          </TouchableOpacity>
        </View>
      </View>
      </View>

      </ScrollView>
      <DatePickerModal
        visible={showDatePicker}
        title="Select Date of Birth"
        initialValue={toDatePickerValue(formData.dob)}
        yearsBack={18}
        accentColor={appConfig.getNavButtonColor()}
        onCancel={() => setShowDatePicker(false)}
        onConfirm={(nextValue) => {
          setFormData((current) => ({ ...current, dob: fromDatePickerValue(nextValue) }));
          setShowDatePicker(false);
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#1e293b', // Same as welcome screen
  },
  scrollContainer: {
    flex: 1,
    zIndex: 1, // Ensure scroll content appears above background
  },
  contentContainer: {
    flexGrow: 1,
    minHeight: '100%', // Ensure content takes full height
  },
  backgroundImageContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#3b82f6', // Rich blue background
  },
  backgroundOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(59, 130, 246, 0.15)', // Light blue overlay for depth
  },
  header: {
    paddingTop: 24 + appConfig.getMobileHeaderTopPadding(),
    paddingBottom: 24,
    paddingHorizontal: 24,
    backgroundColor: 'transparent',
    borderBottomWidth: 0,
    zIndex: 1, // Ensure content appears above background
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#ffffff', // White text
    marginBottom: 8,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  subtitle: {
    fontSize: 16,
    color: '#e2e8f0', // Light gray for subtitle
    lineHeight: 24,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  form: {
    padding: 24,
    zIndex: 1, // Ensure content appears above background
  },
  fieldContainer: {
    marginBottom: 24,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff', // White labels
    marginBottom: 8,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  input: {
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.3)', // Semi-transparent white border
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.9)', // Semi-transparent white background
    color: '#1f2937', // Dark text for readability
  },
  inputError: {
    borderColor: '#fca5a5', // Lighter red for better contrast
    backgroundColor: 'rgba(254, 226, 226, 0.9)', // Light red tint
  },
  errorText: {
    color: '#fca5a5', // Lighter red for better contrast
    fontSize: 14,
    marginTop: 4,
    textShadowColor: 'rgba(0, 0, 0, 0.5)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 1,
  },
  helpText: {
    color: '#cbd5e1', // Lighter gray for help text
    fontSize: 14,
    marginTop: 4,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 1,
  },
  genderContainer: {
    flexDirection: 'row',
    gap: 16,
  },
  genderButton: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.3)',
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    alignItems: 'center',
  },
  genderButtonSelected: {
    borderColor: '#10b981', // Green for selected state
    backgroundColor: '#10b981',
  },
  genderButtonText: {
    fontSize: 16,
    color: '#1f2937', // Dark text for readability
    fontWeight: '500',
  },
  genderButtonTextSelected: {
    color: '#ffffff',
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 16,
    marginTop: 32,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 16,
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.5)',
    borderRadius: 8,
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff', // White text
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  createButton: {
    flex: 1,
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: 'center',
    backgroundColor: '#10b981', // Green for create action
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  createButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },
  // Date picker styles
  dateInput: {
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.3)', // Semi-transparent white border
    borderRadius: 8,
    paddingVertical: 16,
    paddingHorizontal: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.9)', // Semi-transparent white background
    justifyContent: 'center',
  },
  dateText: {
    fontSize: 16,
    color: '#1f2937', // Dark text for readability
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  checkboxBox: {
    width: 24,
    height: 24,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.7)',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxBoxChecked: {
    backgroundColor: '#10b981',
    borderColor: '#10b981',
  },
  checkboxCheck: {
    color: '#ffffff',
    fontWeight: '700',
  },
  checkboxLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  weekChipRow: {
    flexDirection: 'row',
    gap: 8,
    paddingRight: 8,
  },
  gestationScrollRow: {
    marginTop: 6,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  gestationScrollRowMobile: {
    gap: 0,
  },
  gestationScrollView: {
    flex: 1,
  },
  gestationScrollButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.5)',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  gestationScrollButtonText: {
    color: '#1f2937',
    fontWeight: '700',
    fontSize: 11,
  },
  weekChip: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 16,
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.5)',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
  },
  weekChipActive: {
    borderColor: '#10b981',
    backgroundColor: '#10b981',
  },
  weekChipText: {
    color: '#1f2937',
    fontWeight: '600',
  },
  weekChipTextActive: {
    color: '#ffffff',
  },
});